import type { Tone } from '../tone';

export type OrderState =
  | 'production' // at least one kit is not yet Ready, paid or not
  | 'ready_deliver' // all kits Ready, not yet paid
  | 'held' // stopped by accounts or reception
  | 'paid' // all kits Ready and cleared (paid, or released unpaid), awaiting dispatch
  | 'dispatched'; // gone, moves to Archive

export const ORDER_STATES: readonly OrderState[] = [
  'production',
  'ready_deliver',
  'held',
  'paid',
  'dispatched',
];

export interface OrderStateInput {
  kitsAllReady: boolean;
  dispatched: boolean;
  held: boolean;
  /**
   * Paid, or released by Accounts to be shipped before it is paid: either
   * clears a finished case for dispatch. The lab's request of 17 October 2026,
   * item 8. Whether it is paid is `payment.paid`, which this does not change.
   */
  cleared: boolean;
}

/**
 * A hold OUTRANKS payment. An order on hold is never offered for dispatch,
 * whatever else is true about it. Only releasing the hold puts it back.
 *
 * The order of these tests is the rule. Section 7.10 item 9 makes the server
 * enforce the same thing, because hiding the dispatch button is not the same
 * as making dispatch impossible.
 *
 * Paid is a case that is paid AND finished: every kit Ready, waiting to be
 * dispatched. A case paid before its work is done is still being made, and is
 * in production until its last kit is Ready. It read as paid, so the lists
 * said Pending dispatch of a case that was at a bench, and late; the Logistics
 * card counted it, and the dispatch desk was offered a case the server then
 * refused to dispatch. Whether a case is paid is `payment.paid`, which the
 * Payments lists read and this does not change. The request of 8 October 2026
 * (third), and the API's `OrderStateCalculator`.
 *
 * Since the request of 17 October 2026, item 8, a case released by Accounts to
 * be shipped unpaid is cleared as a paid one is, so `paid` is the state of a
 * finished case either paid or released: pending dispatch.
 */
export function orderState(o: OrderStateInput): OrderState {
  if (o.dispatched) return 'dispatched';
  if (o.held) return 'held';
  if (o.kitsAllReady) return o.cleared ? 'paid' : 'ready_deliver';
  return 'production';
}

export const ORDER_STATE_LABEL: Record<OrderState, { label: string; tone: Tone }> = {
  production: { label: 'In production', tone: 'watch' },
  ready_deliver: { label: 'Ready to Deliver', tone: 'watch' },
  held: { label: 'On hold', tone: 'hold' },
  // Pending dispatch, as the status column says it: a released case is in this state unpaid.
  paid: { label: 'Pending dispatch', tone: 'ok' },
  dispatched: { label: 'Dispatched', tone: 'done' },
};

/** A dispatched order leaves the active Orders list and appears in Archive. */
export function isActiveOrderState(state: OrderState): boolean {
  return state !== 'dispatched';
}

/**
 * Whether an order belongs in the Logistics column: past Ready and not yet
 * arrived. Section 2.3 of the request of 13 September 2026.
 *
 * The split between Ready and Logistics has to be stated somewhere or the two
 * double count. Ready is the finished tray the lab is still waiting on accounts
 * for, `ready_deliver`. Logistics is everything that has moved on: paid and
 * waiting for a courier, gone and not arrived, and held AFTER Ready, which
 * appears here because the dispatch desk needs to see it. A delivered order is
 * finished business and is in neither.
 */
export function inLogistics(order: {
  state: OrderState;
  kitsAllReady: boolean;
  delivered: boolean;
}): boolean {
  if (order.delivered) return false;
  if (order.state === 'paid' || order.state === 'dispatched') return true;
  // A hold before Ready is a case stopped in production, not a case in logistics.
  return order.state === 'held' && order.kitsAllReady;
}

/** Section 7.5. Held and not-yet-ready orders are refused at the API, not just hidden. */
export function canDispatch(state: OrderState): boolean {
  return state === 'paid';
}
